'use client';

import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, X } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

export function RadioPlayer() {
    // We fetch settings directly here or could use Context. 
    // For simplicity and to ensure it's decoupled, let's fetch on mount.
    const [config, setConfig] = useState<{ url: string; enabled: boolean } | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [error, setError] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        // Fetch Radio Config
        const supabase = createClient();
        async function fetchConfig() {
            const { data } = await supabase.from('site_settings').select('radio_url, is_radio_enabled').single();
            if (data && data.is_radio_enabled && data.radio_url) {
                setConfig({ url: data.radio_url, enabled: true });
            }
        }
        fetchConfig();
    }, []);

    const togglePlay = () => {
        if (!audioRef.current || !config) return;

        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        setIsPlaying(true);
                        setError(false);
                    })
                    .catch((err) => {
                        console.error("Radio play error:", err);
                        setError(true);
                        setIsPlaying(false);
                    });
            }
        }
    };

    const toggleMute = () => {
        if (!audioRef.current) return;
        audioRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    if (!config || !config.enabled || !isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 pointer-events-none flex justify-center">
            <div className="pointer-events-auto bg-black/90 backdrop-blur-md border border-white/10 rounded-full shadow-2xl pl-2 pr-6 py-2 flex items-center gap-4 max-w-md w-full animate-in slide-in-from-bottom duration-500">

                {/* Play/Pause Button */}
                <button
                    onClick={togglePlay}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isPlaying
                            ? 'bg-primary text-black shadow-[0_0_15px_rgba(234,179,8,0.5)]'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                >
                    {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-1" />}
                </button>

                {/* Info Text */}
                <div className="flex-grow overflow-hidden">
                    <div className="flex flex-col">
                        <span className="text-[10px] text-primary font-bold uppercase tracking-widest leading-none mb-1">
                            {isPlaying ? 'EN DIRECT' : 'RADIO DAALÙ SÉBI'}
                        </span>
                        {error ? (
                            <span className="text-red-400 text-xs font-bold truncate">Flux indisponible</span>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="text-white text-sm font-bold truncate">Radio Communautaire</span>
                                {isPlaying && (
                                    <div className="flex items-end gap-[2px] h-3 pb-[1px]">
                                        <div className="w-[3px] bg-primary animate-[music-bar_0.8s_ease-in-out_infinite]" />
                                        <div className="w-[3px] bg-primary animate-[music-bar_1.1s_ease-in-out_infinite]" />
                                        <div className="w-[3px] bg-primary animate-[music-bar_1.3s_ease-in-out_infinite]" />
                                        <div className="w-[3px] bg-primary animate-[music-bar_0.9s_ease-in-out_infinite]" />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-2 border-l border-white/10 pl-4">
                    <button onClick={toggleMute} className="text-gray-400 hover:text-white transition-colors">
                        {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                    </button>
                    <button onClick={() => setIsVisible(false)} className="text-gray-400 hover:text-red-400 transition-colors ml-2">
                        <X size={18} />
                    </button>
                </div>

                <audio
                    ref={audioRef}
                    src={config.url}
                    preload="none"
                    onError={() => setError(true)}
                />
            </div>

            <style jsx global>{`
                @keyframes music-bar {
                    0%, 100% { height: 20%; }
                    50% { height: 100%; }
                }
            `}</style>
        </div>
    );
}
